/**
 * Chess Board Component
 * Renders the chess board with pieces and handles move input
 */

import React, { useState, useCallback } from 'react';
import type { GameState, Color, Square, Piece, ChessMove, PieceType } from '@jester-chess/shared';
import type { ClientSocket } from '../websocket/ClientSocket';
import { BoardSquare } from './BoardSquare';
import { PawnPromotion } from './PawnPromotion';
import { CapturedPieces } from './CapturedPieces';
import './ChessBoard.css';

interface ChessBoardProps {
  gameState: GameState;
  yourColor: Color;
  socket: ClientSocket | null;
}

export const ChessBoard: React.FC<ChessBoardProps> = ({
  gameState,
  yourColor,
  socket,
}) => {
  const [selectedSquare, setSelectedSquare] = useState<Square | null>(null);
  const [validMoves, setValidMoves] = useState<Square[]>([]);
  const [pendingPromotion, setPendingPromotion] = useState<{ from: Square; to: Square; piece: Piece } | null>(null);

  const files: Array<'a' | 'b' | 'c' | 'd' | 'e' | 'f' | 'g' | 'h'> = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
  const ranks: number[] = [8, 7, 6, 5, 4, 3, 2, 1];

  // Flip board if playing black
  const displayRanks = yourColor === 'black' ? [...ranks].reverse() : ranks;
  const displayFiles = yourColor === 'black' ? [...files].reverse() : files;

  const getPieceAt = useCallback((square: Square): Piece | undefined => {
    // Check both board array and jester references
    const boardPiece = gameState.board.find(
      (p) => p.square.file === square.file && p.square.rank === square.rank
    );
    if (boardPiece) return boardPiece;
    
    // Check jesters
    if (gameState.whiteJester && 
        gameState.whiteJester.square.file === square.file && 
        gameState.whiteJester.square.rank === square.rank) {
      return gameState.whiteJester;
    }
    if (gameState.blackJester && 
        gameState.blackJester.square.file === square.file && 
        gameState.blackJester.square.rank === square.rank) {
      return gameState.blackJester;
    }
    
    return undefined;
  }, [gameState]);

  const handleSquareClick = useCallback((square: Square) => {
    if (!socket || gameState.currentTurn !== yourColor) return;
    if (gameState.status !== 'active') return;

    const piece = getPieceAt(square);

    // If clicking on a selected square, deselect
    if (selectedSquare && 
        selectedSquare.file === square.file && 
        selectedSquare.rank === square.rank) {
      setSelectedSquare(null);
      setValidMoves([]);
      return;
    }

    // If clicking on a valid move square, make the move
    if (selectedSquare && validMoves.some(
      (move) => move.file === square.file && move.rank === square.rank
    )) {
      const fromPiece = getPieceAt(selectedSquare);
      if (fromPiece && fromPiece.type !== 'jester') {
        const promotionRank = fromPiece.color === 'white' ? 8 : 1;
        
        // Check if this is a pawn promotion
        if (fromPiece.type === 'pawn' && square.rank === promotionRank) {
          // Store the move and show promotion UI
          setPendingPromotion({
            from: selectedSquare,
            to: square,
            piece: fromPiece,
          });
          setSelectedSquare(null);
          setValidMoves([]);
          return;
        }
        
        // Normal move
        const move: ChessMove = {
          from: selectedSquare,
          to: square,
          piece: fromPiece,
        };
        socket.sendChessMove(move);
        setSelectedSquare(null);
        setValidMoves([]);
        return;
      }
    }

    // If clicking on own piece, select it and show valid moves
    if (piece && piece.color === yourColor && piece.type !== 'jester') {
      setSelectedSquare(square);
      // Calculate valid moves (simplified - would need server validation for full accuracy)
      setValidMoves(calculateValidMoves(gameState, piece, square));
    } else {
      setSelectedSquare(null);
      setValidMoves([]);
    }
  }, [socket, gameState, yourColor, selectedSquare, validMoves, getPieceAt]);

  const isSquareSelected = useCallback((square: Square): boolean => {
    return selectedSquare !== null &&
      selectedSquare.file === square.file &&
      selectedSquare.rank === square.rank;
  }, [selectedSquare]);

  const isValidMoveSquare = useCallback((square: Square): boolean => {
    return validMoves.some(
      (move) => move.file === square.file && move.rank === square.rank
    );
  }, [validMoves]);

  const isSquareLight = useCallback((fileIndex: number, rankIndex: number): boolean => {
    return (fileIndex + rankIndex) % 2 === 0;
  }, []);

  return (
    <div className="chess-board-container">
      {/* Captured pieces - left side on desktop */}
      <div className="captured-left">
        <CapturedPieces gameState={gameState} yourColor={yourColor} />
      </div>

      <div className="chess-board">
        {/* Rank labels (left side) */}
        <div className="rank-labels-left">
          {displayRanks.map((rank) => (
            <div key={rank} className="rank-label">
              {rank}
            </div>
          ))}
        </div>

        {/* Board wrapper: squares + file labels */}
        <div className="board-wrapper">
          {/* Board squares */}
          <div className="board-squares">
            {displayRanks.map((rank, rankIndex) => (
              <div key={rank} className="rank">
                {displayFiles.map((file, fileIndex) => {
                  const square: Square = { file, rank: rank as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 };
                  const piece = getPieceAt(square);
                  const isLight = isSquareLight(fileIndex, rankIndex);
                  const selected = isSquareSelected(square);
                  const validMove = isValidMoveSquare(square);

                  return (
                    <BoardSquare
                      key={`${file}${rank}`}
                      square={square}
                      piece={piece}
                      isLight={isLight}
                      selected={selected}
                      validMove={validMove}
                      onClick={handleSquareClick}
                      yourColor={yourColor}
                    />
                  );
                })}
              </div>
            ))}
          </div>

          {/* File labels (bottom) */}
          <div className="file-labels-bottom">
            {displayFiles.map((file) => (
              <div key={file} className="file-label">
                {file.toUpperCase()}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Captured pieces - mobile (below board) */}
      <div className="captured-mobile">
        <CapturedPieces gameState={gameState} yourColor={yourColor} />
      </div>

      {pendingPromotion && (
        <PawnPromotion
          color={pendingPromotion.piece.color}
          onSelect={(pieceType: PieceType) => {
            const move: ChessMove = {
              from: pendingPromotion.from,
              to: pendingPromotion.to,
              piece: pendingPromotion.piece,
              promotion: pieceType,
            };
            socket?.sendChessMove(move);
            setPendingPromotion(null);
          }}
        />
      )}
    </div>
  );
};

/**
 * Calculate valid moves for a piece (simplified client-side calculation)
 * Full validation happens on server
 */
function calculateValidMoves(
  gameState: GameState,
  piece: Piece,
  from: Square
): Square[] {
  const moves: Square[] = [];
  const files: Array<'a' | 'b' | 'c' | 'd' | 'e' | 'f' | 'g' | 'h'> = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];

  const getPieceAt = (square: Square): Piece | undefined => {
    const boardPiece = gameState.board.find(
      (p) => p.square.file === square.file && p.square.rank === square.rank
    );
    if (boardPiece) return boardPiece;
    if (gameState.whiteJester && gameState.whiteJester.square.file === square.file && gameState.whiteJester.square.rank === square.rank) {
      return gameState.whiteJester;
    }
    if (gameState.blackJester && gameState.blackJester.square.file === square.file && gameState.blackJester.square.rank === square.rank) {
      return gameState.blackJester;
    }
    return undefined;
  };

  const isEmpty = (square: Square): boolean => {
    return getPieceAt(square) === undefined;
  };

  const canCapture = (square: Square): boolean => {
    const targetPiece = getPieceAt(square);
    if (!targetPiece) return false;
    // Can capture opponent pieces (but not jesters - regular pieces cannot capture jesters)
    if (targetPiece.color === piece.color) return false;
    if (targetPiece.type === 'jester') return false;
    return true;
  };

  // Simple move calculation (full validation on server)
  switch (piece.type) {
    case 'pawn':
      // Pawn moves forward one or two squares
      const direction = piece.color === 'white' ? 1 : -1;
      const startRank = piece.color === 'white' ? 2 : 7;
      
      // One square forward (if empty)
      const oneForward: Square = { file: from.file, rank: (from.rank + direction) as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 };
      if (from.rank + direction >= 1 && from.rank + direction <= 8 && isEmpty(oneForward)) {
        moves.push(oneForward);
      }
      
      // Two squares from start (if both empty)
      if (from.rank === startRank && from.rank + 2 * direction >= 1 && from.rank + 2 * direction <= 8) {
        const twoForward: Square = { file: from.file, rank: (from.rank + 2 * direction) as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 };
        if (isEmpty(oneForward) && isEmpty(twoForward)) {
          moves.push(twoForward);
        }
      }

      // Diagonal captures - pawns can only move diagonally to capture
      const leftFileIndex = files.indexOf(from.file) - 1;
      const rightFileIndex = files.indexOf(from.file) + 1;
      if (leftFileIndex >= 0 && from.rank + direction >= 1 && from.rank + direction <= 8) {
        const leftCapture: Square = { file: files[leftFileIndex], rank: (from.rank + direction) as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 };
        const leftPiece = getPieceAt(leftCapture);
        // Pawns can only move diagonally if there's an opponent piece to capture (not jesters)
        if (leftPiece && leftPiece.color !== piece.color && leftPiece.type !== 'jester') {
          moves.push(leftCapture);
        }
      }
      if (rightFileIndex < 8 && from.rank + direction >= 1 && from.rank + direction <= 8) {
        const rightCapture: Square = { file: files[rightFileIndex], rank: (from.rank + direction) as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 };
        const rightPiece = getPieceAt(rightCapture);
        // Pawns can only move diagonally if there's an opponent piece to capture (not jesters)
        if (rightPiece && rightPiece.color !== piece.color && rightPiece.type !== 'jester') {
          moves.push(rightCapture);
        }
      }

      // En passant capture
      if (gameState.enPassantTarget) {
        const enPassantSquare = gameState.enPassantTarget;
        const enPassantRank = piece.color === 'white' ? 5 : 4; // Pawn must be on 5th rank (white) or 4th rank (black)
        
        if (from.rank === enPassantRank) {
          // Check if en passant target is diagonally adjacent
          const fromFileIndex = files.indexOf(from.file);
          const targetFileIndex = files.indexOf(enPassantSquare.file);
          const fileDiff = Math.abs(targetFileIndex - fromFileIndex);
          const expectedTargetRank = piece.color === 'white' ? 6 : 3; // En passant target is one rank forward
          
          if (fileDiff === 1 && enPassantSquare.rank === expectedTargetRank) {
            moves.push(enPassantSquare);
          }
        }
      }
      break;

    case 'knight':
      // Knight L-shaped moves (can capture)
      const knightMoves = [
        [2, 1], [2, -1], [-2, 1], [-2, -1],
        [1, 2], [1, -2], [-1, 2], [-1, -2]
      ];
      knightMoves.forEach(([df, dr]) => {
        const fileIndex = files.indexOf(from.file);
        const newFileIndex = fileIndex + df;
        const newRank = from.rank + dr;
        if (newFileIndex >= 0 && newFileIndex < 8 && newRank >= 1 && newRank <= 8) {
          const targetSquare: Square = { file: files[newFileIndex], rank: newRank as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 };
          const targetPiece = getPieceAt(targetSquare);
          // Can move to empty square or capture opponent piece (but not jesters)
          if (!targetPiece || (targetPiece.color !== piece.color && targetPiece.type !== 'jester')) {
            moves.push(targetSquare);
          }
        }
      });
      break;

    case 'king':
      // King moves one square in any direction (can capture)
      for (let df = -1; df <= 1; df++) {
        for (let dr = -1; dr <= 1; dr++) {
          if (df === 0 && dr === 0) continue;
          const fileIndex = files.indexOf(from.file);
          const newFileIndex = fileIndex + df;
          const newRank = from.rank + dr;
          if (newFileIndex >= 0 && newFileIndex < 8 && newRank >= 1 && newRank <= 8) {
            const targetSquare: Square = { file: files[newFileIndex], rank: newRank as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 };
            const targetPiece = getPieceAt(targetSquare);
            // Can move to empty square or capture opponent piece (but not jesters)
            if (!targetPiece || (targetPiece.color !== piece.color && targetPiece.type !== 'jester')) {
              moves.push(targetSquare);
            }
          }
        }
      }
      
      // Add castling moves if available (simplified client-side check - full validation on server)
      const castlingMoves = getCastlingMoves(gameState, piece, from, getPieceAt, isEmpty);
      moves.push(...castlingMoves);
      break;

    case 'rook':
      // Rook moves horizontally and vertically
      const rookDirections = [[0, 1], [0, -1], [1, 0], [-1, 0]];
      for (const [df, dr] of rookDirections) {
        for (let i = 1; i < 8; i++) {
          const fileIndex = files.indexOf(from.file);
          const newFileIndex = fileIndex + df * i;
          const newRank = from.rank + dr * i;
          if (newFileIndex < 0 || newFileIndex >= 8 || newRank < 1 || newRank > 8) break;
          const targetSquare: Square = { file: files[newFileIndex], rank: newRank as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 };
          const targetPiece = getPieceAt(targetSquare);
          if (!targetPiece) {
            moves.push(targetSquare);
          } else {
            // Can capture opponent piece (but not jesters)
            if (targetPiece.color !== piece.color && targetPiece.type !== 'jester') {
              moves.push(targetSquare);
            }
            break; // Blocked by piece
          }
        }
      }
      break;

    case 'bishop':
      // Bishop moves diagonally
      const bishopDirections = [[1, 1], [1, -1], [-1, 1], [-1, -1]];
      for (const [df, dr] of bishopDirections) {
        for (let i = 1; i < 8; i++) {
          const fileIndex = files.indexOf(from.file);
          const newFileIndex = fileIndex + df * i;
          const newRank = from.rank + dr * i;
          if (newFileIndex < 0 || newFileIndex >= 8 || newRank < 1 || newRank > 8) break;
          const targetSquare: Square = { file: files[newFileIndex], rank: newRank as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 };
          const targetPiece = getPieceAt(targetSquare);
          if (!targetPiece) {
            moves.push(targetSquare);
          } else {
            // Can capture opponent piece (but not jesters)
            if (targetPiece.color !== piece.color && targetPiece.type !== 'jester') {
              moves.push(targetSquare);
            }
            break; // Blocked by piece
          }
        }
      }
      break;

    case 'queen':
      // Queen moves like rook + bishop
      const queenDirections = [[0, 1], [0, -1], [1, 0], [-1, 0], [1, 1], [1, -1], [-1, 1], [-1, -1]];
      for (const [df, dr] of queenDirections) {
        for (let i = 1; i < 8; i++) {
          const fileIndex = files.indexOf(from.file);
          const newFileIndex = fileIndex + df * i;
          const newRank = from.rank + dr * i;
          if (newFileIndex < 0 || newFileIndex >= 8 || newRank < 1 || newRank > 8) break;
          const targetSquare: Square = { file: files[newFileIndex], rank: newRank as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 };
          const targetPiece = getPieceAt(targetSquare);
          if (!targetPiece) {
            moves.push(targetSquare);
          } else {
            // Can capture opponent piece (but not jesters)
            if (targetPiece.color !== piece.color && targetPiece.type !== 'jester') {
              moves.push(targetSquare);
            }
            break; // Blocked by piece
          }
        }
      }
      break;

    default:
      break;
  }

  return moves;
}

/**
 * Get available castling moves for king (client-side check)
 * Full validation happens on server
 */
function getCastlingMoves(
  gameState: GameState,
  piece: Piece,
  from: Square,
  getPieceAt: (square: Square) => Piece | undefined,
  isEmpty: (square: Square) => boolean
): Square[] {
  const moves: Square[] = [];
  
  // King must be on starting square
  const expectedRank = piece.color === 'white' ? 1 : 8;
  if (from.file !== 'e' || from.rank !== expectedRank) {
    return moves;
  }

  // Check if castling rights exist
  const rights = gameState.castlingRights;
  if (!rights) return moves;

  // Kingside castling (O-O): King moves e -> g, requires h-rook and f,g empty
  const canKingside = piece.color === 'white' ? rights.whiteKingside : rights.blackKingside;
  if (canKingside) {
    const fSquare: Square = { file: 'f', rank: expectedRank as 1 | 8 };
    const gSquare: Square = { file: 'g', rank: expectedRank as 1 | 8 };
    const hSquare: Square = { file: 'h', rank: expectedRank as 1 | 8 };
    
    const hRook = getPieceAt(hSquare);
    
    if (isEmpty(fSquare) && 
        isEmpty(gSquare) &&
        hRook && hRook.type === 'rook' && hRook.color === piece.color) {
      moves.push(gSquare);
    }
  }

  // Queenside castling (O-O-O): King moves e -> c, requires a-rook and b,c,d empty
  const canQueenside = piece.color === 'white' ? rights.whiteQueenside : rights.blackQueenside;
  if (canQueenside) {
    const bSquare: Square = { file: 'b', rank: expectedRank as 1 | 8 };
    const cSquare: Square = { file: 'c', rank: expectedRank as 1 | 8 };
    const dSquare: Square = { file: 'd', rank: expectedRank as 1 | 8 };
    const aSquare: Square = { file: 'a', rank: expectedRank as 1 | 8 };
    
    const aRook = getPieceAt(aSquare);
    
    if (isEmpty(bSquare) && 
        isEmpty(cSquare) && 
        isEmpty(dSquare) &&
        aRook && aRook.type === 'rook' && aRook.color === piece.color) {
      moves.push(cSquare);
    }
  }

  return moves;
}
